import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Skeleton, Stack, Typography } from '@mui/material';
import { AnswerComponent } from '../Answer';
import { FullScreenDialog } from '../../FullScreenDialog/FullScreenDialog';
import { LocalizationContext } from '@context/LocalizationContext';
import { refineFonts } from '@/Theming/refineFonts';
import { useQueryGetAttachmentURL } from '@hooks/useQueryGetAttachmentURL';
import { useAnalytics } from '@hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from '@/types/RefineAnalyticsEvents';
import { useQueryLog } from '@hooks/useQueryLog';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { InvestigationContext } from '@context/InvestigationContext';
import { EnlargeButtonWithTextOnImage } from './EnlargeButtonVariants';

interface Props {
	setCurrentAnswer: (answerId: any, answerText?: any) => void;
	setCheckSubmittingCheckbox?: (value: boolean) => void;
	isTextInputInvalid: boolean;
	isCheckboxInvalid: boolean;
}

export const CheckQuestion: FC<Props> = ({ setCurrentAnswer, setCheckSubmittingCheckbox, isCheckboxInvalid }) => {
	const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
	const { OUCF } = useContext(LocalizationContext);
	const [checkImageURL, setCheckImageURL] = useState<string>('');
	const [isFullScreenOpen, setFullScreenOpen] = useState(false);
	const [checked, setChecked] = useState(false);
	const { trackRefineEvent } = useAnalytics();
	const [isCheckLoading, setIsCheckLoading] = useState(true);
	const { sendLogToBackend } = useQueryLog();
	const { investigationDetails, setSupportLandscape } = useContext(InvestigationContext);
	const enlargeCheckImageVariant = useFeatureFlagVariantKey('enlargeCheckImageVariant');

	const handleEnlargeButtonClick = () => {
		setSupportLandscape(true);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_CUSTOMER_ELRAGES_CHECK_IMAGE);
		setFullScreenOpen(true);
	};

	const { getAttachment } = useQueryGetAttachmentURL();

	const possibleAnswers: string[] = useMemo(() => {
		if (investigationDetails && investigationDetails.nextQuestion) {
			return Object.keys(investigationDetails.nextQuestion.possibleAnswers);
		}

		return [];
	}, [investigationDetails]);

	useEffect(() => {
		if (scrollTargetRef.current) {
			setTimeout(() => {
				scrollTargetRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
					inline: 'nearest',
				});
			}, 100);
		}
	}, [selectedAnswerId]);

	useEffect(() => {
		if (errorTextRef.current && isCheckboxInvalid) {
			errorTextRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest',
			});
		}
	}, [isCheckboxInvalid]);

	useEffect(() => {
		if (investigationDetails?.nextQuestion.additionalOptions?.checkData) {
			getAttachment(investigationDetails.nextQuestion.additionalOptions.checkData.checkImageId)
				.then((res) => {
					trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_CHECK_IMAGE_LOADED);
					setCheckImageURL(res.signedURL);
				})
				.catch((error) => {
					trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_IMAGE_LOADING_ERROR);
					sendLogToBackend(`Check image loading error`, 'ERROR');
				});
		}
	}, [investigationDetails?.nextQuestion.additionalOptions?.checkData]);

	function setSelectedAnswer(answerId: string, answerText: string) {
		setSelectedAnswerId(answerId);
		setChecked(false);
		const question = investigationDetails?.nextQuestion;

		let openText: string | null = '';
		if (question && question.possibleAnswers) {
			const selectedAnswer = question.possibleAnswers[answerId];
			if (selectedAnswer.icon === 'NotAcceptCheck' && selectedAnswer.answerId === '2') {
				sessionStorage.setItem('checkValidation', 'false');
				trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_CUSTOMER_ANSWERED_NO);
			}
			if (selectedAnswer.icon === 'AcceptCheck' && selectedAnswer.answerId === '1') {
				sessionStorage.setItem('checkValidation', 'true');
				trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_CUSTOMER_ANSWERED_YES);
			}
			if (question.possibleAnswers[answerId ?? ''].isOpen) {
				openText = answerText;
			} else {
				openText = '';
			}
		}
		setCurrentAnswer(answerId, openText);
	}

	const answerContainerRef = useRef<any>(null);
	const scrollTargetRef = useRef<any>(null);
	const errorTextRef = useRef<any>(null);

	const textInputWidth = useMemo(() => {
		const answerWidth = answerContainerRef.current?.getBoundingClientRect().width;
		//If we don't have the answer width, we set the width to 100% to avoid layout shift
		if (!answerWidth) {
			return '100%';
		}
		return `${answerWidth * 2}px`;
	}, [answerContainerRef.current]);

	const questionText = investigationDetails?.nextQuestion.questionText;

	useEffect(() => {
		setKey((prev) => prev + 1);
	}, [questionText]);

	// Reset selected answer when moving to a new check
	// This ensures each check starts with a clean slate (no pre-selected answer)
	useEffect(() => {
		setSelectedAnswerId(null);
		setChecked(false);
	}, [investigationDetails?.nextQuestion?.qid]);

	const [key, setKey] = useState(0);

	const handleCloseFullScreenDialog = () => {
		setFullScreenOpen(false);
		setSupportLandscape(false);
	};

	const handleStackClick = () => {
		setChecked((prev) => !prev);
		setCheckSubmittingCheckbox!(!checked);
	};
	return (
		<Stack
			key={key}
			overflow={'scroll'}
			flexGrow={1}
			maxWidth={'100%'}
			gap={'12px'}
			justifyContent={'start'}
			alignItems={'center'}
			justifyItems={'start'}
			width={'100%'}>
			<Typography textAlign={'center'} variant={'questionText'}>
				{questionText}
			</Typography>
			<Box
				sx={{
					position: 'relative',
					paddingInline: '2px',
				}}
				maxHeight={'220px'}>
				{isCheckLoading && (
					<Skeleton sx={{ width: '100vw', height: '150px', maxWidth: '550px' }} animation={'wave'} />
				)}
				<img
					data-cy={'oucfCheckImage'}
					onLoad={() => {
						setIsCheckLoading(false);
					}}
					style={{
						display: isCheckLoading ? 'none' : 'block',
						maxHeight: '220px',
						objectFit: 'cover',
						borderRadius: '10px',
						border: '1.5px solid #B2C5FF',
						boxShadow: '0px 0px 6px 0px rgba(15, 21, 47, 0.05), 0px 3px 6px 0px rgba(15, 21, 47, 0.05)',
					}}
					width={'100%'}
					src={checkImageURL}
					alt="Check"
				/>

				<FullScreenDialog
					isOpen={isFullScreenOpen}
					onClose={handleCloseFullScreenDialog}
					imageResourcesPath={checkImageURL}
				/>
				{!isCheckLoading && (
					<EnlargeButtonWithTextOnImage
						buttonText={OUCF.EnlargeCheckButtonText}
						onClick={handleEnlargeButtonClick}
					/>
				)}
			</Box>
			<Stack direction={'row'} gap={'16px'}>
				{possibleAnswers.map((index) => {
					return (
						<AnswerComponent
							isCheckQuestion={true}
							ref={answerContainerRef}
							key={index}
							index={parseInt(index)}
							disabled={isCheckLoading}
							setAnswer={setSelectedAnswer}
							selectedAnswerId={selectedAnswerId}
							text={investigationDetails?.nextQuestion.possibleAnswers[index].text}
							icon={investigationDetails?.nextQuestion.possibleAnswers[index].icon ?? ''}
							answerId={investigationDetails?.nextQuestion.possibleAnswers[index].answerId}
						/>
					);
				})}
			</Stack>
			{selectedAnswerId === '1' && (
				<Stack gap={'4px'}>
					<Stack
						gap={'4px'}
						direction={'row'}
						alignItems={'center'}
						onClick={handleStackClick}
						justifyContent={'start'}
						sx={{
							width: textInputWidth,
						}}>
						<Checkbox
							checked={checked}
							sx={{
								padding: '0',
								color: isCheckboxInvalid ? '#D23C3C' : '',
								alignSelf: 'start',
							}}
							data-cy={'oucfCheckbox'}
						/>
						<Typography ref={scrollTargetRef} textAlign={'start'} sx={{ ...refineFonts.mediumRegular }}>
							{OUCF.CheckboxText ||
								'I reviewed the payee and the amount above. The check can be processed.'}
						</Typography>
					</Stack>
					{isCheckboxInvalid && (
						<Typography
							ref={errorTextRef}
							sx={{ ...refineFonts.smallRegular, color: '#C02020', paddingLeft: '28px' }}>
							{OUCF.CheckboxErrorText || 'Please click the box to confirm you reviewed the check'}
						</Typography>
					)}
				</Stack>
			)}
		</Stack>
	);
};
